import React, {
  Dispatch,
  SetStateAction,
  ReactElement,
  useEffect,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import COLORS from '~/constants/colors';
import VARIANTS from '~/constants/variants';
import { FONT_SIZES } from '~/constants/typography';
import FramedView from '~/components/FramedView';
import Announcement from '~/components/Announcement';
import Button from '~/components/buttons/Button';
import TextArea from '~/components/inputs/TextArea';
import Divider from '~/components/flow/Divider';
import Title from '~/components/titles/Title';
import Subtext from '~/components/titles/Subtext';
import { stopEventEffects } from '~/utils';

import { Session } from '../models';
import uris from '../uris';

interface ProductionProps {
  session: Session;
  setSession: Dispatch<SetStateAction<Session>>;
}

function Production({ session, setSession }: ProductionProps): ReactElement {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { timings } = state ?? {};
  const mergeTimings = (startTime, endTime, oldTimings, newTimings) => {
    const keep = (timing) => timing < startTime || timing > endTime;
    const compare = (a, b) => a - b;

    return oldTimings.filter(keep).concat(newTimings).sort(compare);
  };
  const mergedTimings = mergeTimings(
    session.videoStartTime,
    session.videoEndTime,
    session.rhythm.timings,
    timings ?? []
  );

  useEffect(() => {
    if (!session.song.source) navigate(uris.studio);
  }, []);

  // Handle production interactions
  const handleAccept = (event) => {
    stopEventEffects(event);
    setSession({
      ...session,
      rhythm: {
        ...session.rhythm,
        timings: mergedTimings,
      },
    });
    navigate(uris.studio);
  };
  const handleDiscard = (event) => {
    stopEventEffects(event);
    navigate(uris.studio);
  };

  // Styling
  const productionStyle = {
    width: 'min(45em, 82.5vw)',
  };
  const frameColors: [string, string] = [
    COLORS.waikawaGray,
    COLORS.waikawaGray,
  ];
  const buttonStyle = {
    width: '9em',
    margin: '0 0.25em 0.38em',
    boxShadow: `-0.1em 0.1em ${COLORS.black25}`,
    borderStyle: 'none',
    fontSize: FONT_SIZES.text,
  };
  const textAreaStyle = {
    width: 'min(320px, 75vw)',
    height: '5em',
  };
  const levelStyle = {
    display: 'block',
    marginTop: '1em',
  };
  const levelRowSpaceStyle = {
    height: '0.25em',
  };
  const betweenStyle = {
    margin: '0.1em 0 0 0.25em',
  };

  return (
    <FramedView accent colors={frameColors} style={productionStyle}>
      <Title>Production</Title>
      <br />
      Please review the results of your recording. Accepting will overwrite the
      old timings between your start and end times with the new timings you just
      made. The others will remain untouched.
      <Divider />
      <div style={levelStyle}>
        Here are your old timings
        <div style={levelRowSpaceStyle} />
        <TextArea
          readOnly
          value={JSON.stringify(session.rhythm.timings)}
          style={textAreaStyle}
        />
      </div>
      <div style={levelStyle}>
        Here are your new timings
        <div style={levelRowSpaceStyle} />
        <TextArea
          readOnly
          value={JSON.stringify(timings)}
          style={textAreaStyle}
        />
        <Subtext style={betweenStyle}>
          Between{' '}
          <b style={{ color: COLORS.dodgerBlue }}>
            {Math.floor(session.videoStartTime / 1000)}
          </b>{' '}
          and{' '}
          <b style={{ color: COLORS.dodgerBlue }}>
            {Math.floor(session.videoEndTime / 1000)}
          </b>{' '}
          seconds
        </Subtext>
      </div>
      <div style={levelStyle}>
        What the merged timings will be
        <div style={levelRowSpaceStyle} />
        <TextArea
          readOnly
          value={JSON.stringify(mergedTimings)}
          style={textAreaStyle}
        />
      </div>
      <br />
      <Announcement header="Note" variant={VARIANTS.warning}>
        This mutates your work! If you&apos;re worried, please copy down your
        old timings somewhere.
      </Announcement>
      <br />
      <Button
        label="Accept"
        onClick={handleAccept}
        onTouchEnd={handleAccept}
        style={buttonStyle}
      />
      <Button
        label="Discard"
        variant={VARIANTS.fail}
        onClick={handleDiscard}
        onTouchEnd={handleDiscard}
        style={buttonStyle}
      />
    </FramedView>
  );
}

export default Production;
